import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumb, Button, Dropdown, Form, Spinner, Tab, Tabs } from "react-bootstrap";
import { Download } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";
import {
  AuthService,
  CustomerService,
  EventsService,
  MedicalFormService,
  VehicleService,
} from "../../services";
import { CUSTOMER_TYPE, isCustomerListActive } from "../../shared";
import { getSiteDisplayName } from "../../shared/constants/siteDisplayNames";
import {
  buildMolstEntry,
  buildMolstFortuneSheetData,
  downloadMolstPdf,
  downloadMolstXlsx,
  hasMolstLegalData,
  sortMolstEntries,
} from "../../shared/utils/molstExport";
import {
  buildCacfpFortuneSheetData,
  downloadCacfpPdf,
  downloadCacfpXlsx,
  getMaxCacfpExportYear,
} from "../../shared/utils/cacfpSheetExport";
import {
  buildVitalSignsFortuneSheetData,
  collectVitalSignsCustomerUpdates,
  downloadVitalSignsPdf,
  downloadVitalSignsXlsx,
  keepVitalSignsRosterRows,
  parseVitalSignsRowsFromWorkbook,
  vitalSignsCustomerDisplayName,
  vitalSignsRowsEqual,
} from "../../shared/utils/vitalSignsSheetExport";
import {
  buildTbFortuneSheetData,
  buildTbRows,
  collectTbCustomerUpdates,
  downloadTbPdf,
  downloadTbXlsx,
  parseTbRowsFromWorkbook,
  tbRowsEqual,
} from "../../shared/utils/tbSheetExport";
import {
  applyAuditDerivedFields,
  auditRowsEqual,
  buildAuditFortuneSheetData,
  buildAuditRows,
  collectAuditCustomerUpdates,
  downloadAuditPdf,
  downloadAuditXlsx,
  parseAuditRowsFromWorkbook,
} from "../../shared/utils/auditSheetExport";
import {
  applyMonthlyNoteDerivedFields,
  buildMonthlyNoteFortuneSheetData,
  buildMonthlyNoteRows,
  collectMonthlyNoteCustomerUpdates,
  downloadMonthlyNotePdf,
  downloadMonthlyNoteXlsx,
  monthlyNoteRowsEqual,
  parseMonthlyNoteRowsFromWorkbook,
} from "../../shared/utils/monthlyNoteSheetExport";
import {
  buildRoundingFortuneSheetData,
  collectRoundingCustomerUpdates,
  downloadRoundingPdf,
  downloadRoundingXlsx,
  parseRoundingRowsFromWorkbook,
  roundingRowsEqual,
  roundingRowsFromApi,
} from "../../shared/utils/roundingSheetExport";
import {
  adcapsRowsEqual,
  applyAdcapsDerivedFields,
  buildAdcapsFortuneSheetData,
  buildAdcapsRows,
  collectAdcapsCustomerUpdates,
  downloadAdcapsPdf,
  downloadAdcapsXlsx,
  parseAdcapsRowsFromWorkbook,
  remainingDaysBackground,
  sortAdcapsRows,
} from "../../shared/utils/adcapsSheetExport";
import {
  buildMedicationFortuneSheetData,
  buildMedicationSections,
  collectMedicationCustomerUpdates,
  downloadMedicationPdf,
  downloadMedicationXlsx,
  emptyMedicationSections,
  medicationSectionsEqual,
  medicationSheetLayout,
  parseMedicationSectionsFromWorkbook,
} from "../../shared/utils/medicationSheetExport";
import { filterMedicalFormCustomerUpdates } from "../../shared/utils/medicalCustomerSheetSave";

const todayIsoDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

function parseIsoLocalDate(value) {
  const [year, month, day] = `${value || ""}`.split("-").map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
}

function toIsoLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getMonday(dateInput) {
  const date = new Date(dateInput);
  date.setHours(0, 0, 0, 0);
  const day = date.getDay();
  date.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
  return date;
}

function formatWeekDate(date) {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

/** FortuneSheet selects A1 on mount; clear that highlight so the title row is not selected. */
function clearFortuneDefaultSelection(workbookApi) {
  try {
    workbookApi?.setSelection?.(undefined);
  } catch (_err) {
    /* ignore */
  }
}

function useClearFortuneDefaultSelection(workbookRef, resetKey) {
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) clearFortuneDefaultSelection(workbookRef.current);
    };
    const timers = [0, 50, 200].map((ms) => window.setTimeout(run, ms));
    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [workbookRef, resetKey]);
}

/** Room kept for FortuneSheet's vertical scrollbar so the last column stays visible. */
const FORTUNE_SCROLLBAR_ALLOWANCE_PX = 20;

function getSheetColumnWidths(sheetData) {
  const sheet = sheetData?.[0];
  const columnlen = sheet?.config?.columnlen;
  if (!columnlen) return null;
  const count = sheet.column || Object.keys(columnlen).length;
  const widths = [];
  for (let c = 0; c < count; c += 1) widths.push(Number(columnlen[`${c}`]) || 100);
  return widths;
}

/**
 * Stretch every column except "No." so the sheet fills the frame, and keep it
 * filled while the window resizes. Widths are pushed through the workbook API
 * instead of rebuilding the sheet, so in-progress edits survive a resize.
 */
function useFitFortuneColumnsToWidth(workbookRef, wrapRef, sheetData, resetKey) {
  const appliedRef = useRef("");
  // Track widths by value: sheet data gets a new identity on every cell edit.
  const widthsKey = (getSheetColumnWidths(sheetData) || []).join(",");

  useEffect(() => {
    const wrap = wrapRef.current;
    const baseWidths = widthsKey ? widthsKey.split(",").map(Number) : null;
    if (!wrap || !baseWidths || baseWidths.length < 2) return undefined;

    appliedRef.current = "";

    const apply = () => {
      const available = wrap.clientWidth - FORTUNE_SCROLLBAR_ALLOWANCE_PX;
      const flexibleBase = baseWidths.slice(1).reduce((sum, width) => sum + width, 0);
      const flexibleTarget = available - baseWidths[0];
      if (!flexibleBase || flexibleTarget <= flexibleBase) return;

      const scale = flexibleTarget / flexibleBase;
      const nextWidths = {};
      let used = baseWidths[0];
      for (let c = 1; c < baseWidths.length; c += 1) {
        // Absorb rounding drift in the last column so the total matches exactly.
        const width =
          c === baseWidths.length - 1
            ? Math.max(40, available - used)
            : Math.round(baseWidths[c] * scale);
        nextWidths[`${c}`] = width;
        used += width;
      }

      const signature = JSON.stringify(nextWidths);
      if (signature === appliedRef.current) return;
      appliedRef.current = signature;
      try {
        workbookRef.current?.setColumnWidth?.(nextWidths, {}, true);
      } catch (_err) {
        /* ignore */
      }
    };

    let frame = 0;
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(apply);
    };

    const timers = [0, 60, 220].map((ms) => window.setTimeout(schedule, ms));
    const observer = new ResizeObserver(schedule);
    observer.observe(wrap);

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [workbookRef, wrapRef, widthsKey, resetKey]);
}

function fortuneViewOrEditProps(canEdit, editProps) {
  // Always pass a hooks object. FortuneSheet core does `ctx.hooks.afterUpdateCell`
  // without optional chaining; omitting hooks leaves ctx.hooks undefined and
  // blanks the page for view-only Medical Form users.
  if (!canEdit) return { allowEdit: false, hooks: {} };
  return { allowEdit: true, ...editProps };
}

class MedicalFormErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("Medical Form failed to render:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="p-4 text-muted">
            Unable to display this sheet. Refresh the page.
          </div>
        )
      );
    }
    return this.props.children;
  }
}

const MedicalFormWorkbook = React.forwardRef((props, ref) => (
  <MedicalFormErrorBoundary>
    <Workbook ref={ref} {...props} />
  </MedicalFormErrorBoundary>
));

function medicalFormCustomerLockHint(permissionLabel) {
  return `Customer Info fields are view-only — editing them requires ${permissionLabel}.`;
}

const MedicalForm = () => {
  const navigate = useNavigate();
  const canView = AuthService.canViewMedicalForm();
  const canEdit = AuthService.canEditMedicalForm();
  const canEditMedicalInsurance = AuthService.canEditCustomerTab("medicalInsurance");
  const canEditPersonalInfo = AuthService.canEditCustomerTab("personalInfo");
  const canEditConfidential = AuthService.canEditCustomerTab("confidentialDetails");
  const canEditMolstSheet = canEdit && canEditMedicalInsurance;
  const canEditTbSheet = canEdit && (canEditPersonalInfo || canEditMedicalInsurance);
  const canEditAuditSheet = canEdit && (canEditConfidential || canEditPersonalInfo);
  const canEditMonthlyNoteSheet = canEdit && canEditPersonalInfo;
  const canEditAdcapsSheet = canEdit && canEditMedicalInsurance;
  const canEditMedicationSheet = canEdit && canEditMedicalInsurance;
  const [activeTab, setActiveTab] = useState("molst");

  // MOLST
  const [molstEntries, setMolstEntries] = useState([]);
  const [loadingMolst, setLoadingMolst] = useState(false);
  const [savingMolst, setSavingMolst] = useState(false);
  const [editedCustomers, setEditedCustomers] = useState({});
  const [molstSheetKey, setMolstSheetKey] = useState(0);

  // CACFP (view-only)
  const maxCacfpYear = useMemo(() => getMaxCacfpExportYear(), []);
  const [cacfpYear, setCacfpYear] = useState(maxCacfpYear);
  const [cacfpPayload, setCacfpPayload] = useState(null);
  const [loadingCacfp, setLoadingCacfp] = useState(false);
  const [cacfpSheetKey, setCacfpSheetKey] = useState(0);

  // Vital Signs (day+site file)
  const [vitalDate, setVitalDate] = useState(todayIsoDate);
  const [vitalPayload, setVitalPayload] = useState(null);
  const [vitalBaselineRows, setVitalBaselineRows] = useState([]);
  const [vitalDraftRows, setVitalDraftRows] = useState([]);
  const [loadingVital, setLoadingVital] = useState(false);
  const [savingVital, setSavingVital] = useState(false);
  const [vitalSheetKey, setVitalSheetKey] = useState(0);

  // TB (editable Customer Info fields)
  const [tbBaselineRows, setTbBaselineRows] = useState([]);
  const [tbDraftRows, setTbDraftRows] = useState([]);
  const [loadingTb, setLoadingTb] = useState(false);
  const [savingTb, setSavingTb] = useState(false);
  const [tbSheetKey, setTbSheetKey] = useState(0);

  // Audit (editable Customer Info fields)
  const [auditBaselineRows, setAuditBaselineRows] = useState([]);
  const [auditDraftRows, setAuditDraftRows] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [savingAudit, setSavingAudit] = useState(false);
  const [auditSheetKey, setAuditSheetKey] = useState(0);

  // Monthly Note (editable Customer Info fields)
  const [monthlyNoteBaselineRows, setMonthlyNoteBaselineRows] = useState([]);
  const [monthlyNoteDraftRows, setMonthlyNoteDraftRows] = useState([]);
  const [loadingMonthlyNote, setLoadingMonthlyNote] = useState(false);
  const [savingMonthlyNote, setSavingMonthlyNote] = useState(false);
  const [monthlyNoteSheetKey, setMonthlyNoteSheetKey] = useState(0);

  // Rounding (editable customer fields + one day+site chart file)
  const [roundingPayload, setRoundingPayload] = useState(null);
  const [roundingBaselineRows, setRoundingBaselineRows] = useState([]);
  const [roundingDraftRows, setRoundingDraftRows] = useState([]);
  const [loadingRounding, setLoadingRounding] = useState(false);
  const [savingRounding, setSavingRounding] = useState(false);
  const [roundingSheetKey, setRoundingSheetKey] = useState(0);

  // ADCAPS (editable LTSS ID + completed date)
  const [adcapsBaselineRows, setAdcapsBaselineRows] = useState([]);
  const [adcapsDraftRows, setAdcapsDraftRows] = useState([]);
  const [loadingAdcaps, setLoadingAdcaps] = useState(false);
  const [savingAdcaps, setSavingAdcaps] = useState(false);
  const [adcapsSheetKey, setAdcapsSheetKey] = useState(0);

  // Medication (editable Type / Note fields on Customer Info)
  const [medicationBaselineSections, setMedicationBaselineSections] = useState(emptyMedicationSections);
  const [medicationDraftSections, setMedicationDraftSections] = useState(emptyMedicationSections);
  const [loadingMedication, setLoadingMedication] = useState(false);
  const [savingMedication, setSavingMedication] = useState(false);
  const [medicationSheetKey, setMedicationSheetKey] = useState(0);

  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);

  const molstWorkbookRef = useRef(null);
  const cacfpWorkbookRef = useRef(null);
  const vitalWorkbookRef = useRef(null);
  const tbWorkbookRef = useRef(null);
  const auditWorkbookRef = useRef(null);
  const monthlyNoteWorkbookRef = useRef(null);
  const roundingWorkbookRef = useRef(null);
  const adcapsWorkbookRef = useRef(null);
  const medicationWorkbookRef = useRef(null);
  const molstWrapRef = useRef(null);
  const cacfpWrapRef = useRef(null);
  const vitalWrapRef = useRef(null);
  const tbWrapRef = useRef(null);
  const auditWrapRef = useRef(null);
  const monthlyNoteWrapRef = useRef(null);
  const roundingWrapRef = useRef(null);
  const adcapsWrapRef = useRef(null);
  const medicationWrapRef = useRef(null);

  const siteNum = EventsService.site;
  const centerName = getSiteDisplayName(siteNum);
  const molstWorkbookKey = `molst-${molstSheetKey}-${molstEntries.length}-${centerName}-${canEditMolstSheet ? "edit" : "view"}`;
  const cacfpWorkbookKey = `cacfp-${cacfpSheetKey}-${cacfpYear}`;
  const vitalWorkbookKey = `vital-${vitalSheetKey}-${vitalDate}-${canEdit ? "edit" : "view"}-${canEditMedicalInsurance ? "mi" : "nmi"}-amdc`;
  const tbWorkbookKey = `tb-${tbSheetKey}-${tbBaselineRows.length}-${siteNum}-${canEditTbSheet ? "edit" : "view"}-${canEditPersonalInfo ? "pi" : "npi"}-${canEditMedicalInsurance ? "mi" : "nmi"}-dd7e6b`;
  const auditWorkbookKey = `audit-${auditSheetKey}-${auditBaselineRows.length}-${siteNum}-${canEditAuditSheet ? "edit" : "view"}-${canEditConfidential ? "cd" : "ncd"}-${canEditPersonalInfo ? "pi" : "npi"}`;
  const monthlyNoteWorkbookKey = `monthly-note-${monthlyNoteSheetKey}-${monthlyNoteBaselineRows.length}-${siteNum}-${canEditMonthlyNoteSheet ? "edit" : "view"}`;
  const roundingWorkbookKey = `rounding-${roundingSheetKey}-${roundingBaselineRows.length}-${siteNum}-${canEdit ? "edit" : "view"}-${canEditMedicalInsurance ? "mi" : "nmi"}`;
  const adcapsWorkbookKey = `adcaps-${adcapsSheetKey}-${adcapsBaselineRows.length}-${siteNum}-${canEditAdcapsSheet ? "edit" : "view"}`;
  const medicationWorkbookKey = `medication-${medicationSheetKey}-${medicationBaselineSections.centerRows.length}-${medicationBaselineSections.bbpRows.length}-${siteNum}-${canEditMedicationSheet ? "edit" : "view"}`;

  useClearFortuneDefaultSelection(molstWorkbookRef, molstWorkbookKey);
  useClearFortuneDefaultSelection(cacfpWorkbookRef, cacfpWorkbookKey);
  useClearFortuneDefaultSelection(vitalWorkbookRef, vitalWorkbookKey);
  useClearFortuneDefaultSelection(tbWorkbookRef, tbWorkbookKey);
  useClearFortuneDefaultSelection(auditWorkbookRef, auditWorkbookKey);
  useClearFortuneDefaultSelection(monthlyNoteWorkbookRef, monthlyNoteWorkbookKey);
  useClearFortuneDefaultSelection(roundingWorkbookRef, roundingWorkbookKey);
  useClearFortuneDefaultSelection(adcapsWorkbookRef, adcapsWorkbookKey);
  useClearFortuneDefaultSelection(medicationWorkbookRef, medicationWorkbookKey);

  useEffect(() => {
    if (!canView) {
      window.alert(
        "You haven't login yet OR this user does not have access to this page. Please change an admin account to login."
      );
      AuthService.logout();
      navigate("/login");
    }
  }, [navigate, canView]);

  useEffect(() => {
    if (!canView || activeTab !== "molst") return;
    let cancelled = false;
    setLoadingMolst(true);
    CustomerService.getAllCustomers()
      .then((response) => {
        if (cancelled) return;
        const allCustomers = Array.isArray(response?.data) ? response.data : [];
        const entries = sortMolstEntries(
          allCustomers
            .filter((customer) => {
              const normalizedType = `${customer?.type ?? ""}`.trim().toLowerCase();
              if (normalizedType !== CUSTOMER_TYPE.MEMBER) return false;
              if (!isCustomerListActive(customer)) return false;
              if (!hasMolstLegalData(customer)) return false;
              return true;
            })
            .map((customer) => buildMolstEntry(customer))
        );
        setMolstEntries(entries);
        setEditedCustomers({});
        setMolstSheetKey((key) => key + 1);
      })
      .catch((error) => {
        console.error("Failed to load MOLST customers:", error);
        if (!cancelled) {
          setMolstEntries([]);
          window.alert(error?.message || "Failed to load MOLST data.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMolst(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, canView]);

  useEffect(() => {
    if (!canView || activeTab !== "cacfp") return;
    let cancelled = false;
    setLoadingCacfp(true);
    VehicleService.getCacfpData(cacfpYear)
      .then((response) => {
        if (cancelled) return;
        setCacfpPayload(response?.data || null);
        setCacfpSheetKey((key) => key + 1);
      })
      .catch((error) => {
        console.error("Failed to load CACFP data:", error);
        if (!cancelled) {
          setCacfpPayload(null);
          window.alert(error?.response?.data?.message || error?.message || "Failed to load CACFP data.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCacfp(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, cacfpYear, canView]);

  useEffect(() => {
    if (!canView || activeTab !== "vital-signs") return;
    let cancelled = false;
    setLoadingVital(true);
    MedicalFormService.getVitalSignsDay(vitalDate)
      .then((response) => {
        if (cancelled) return;
        const data = response?.data || null;
        const rows = keepVitalSignsRosterRows(
          (Array.isArray(data?.rows) ? data.rows : []).map((row) => ({
            ...row,
            name: vitalSignsCustomerDisplayName(row?.name),
          }))
        );
        setVitalPayload(data);
        setVitalBaselineRows(rows);
        setVitalDraftRows(rows);
        setVitalSheetKey((key) => key + 1);
      })
      .catch((error) => {
        console.error("Failed to load Vital Signs data:", error);
        if (!cancelled) {
          setVitalPayload(null);
          setVitalBaselineRows([]);
          setVitalDraftRows([]);
          window.alert(
            error?.response?.data?.message || error?.message || "Failed to load Vital Signs data."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingVital(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, vitalDate, canView]);

  useEffect(() => {
    if (!canView || activeTab !== "tb") return;
    let cancelled = false;
    setLoadingTb(true);
    CustomerService.getAllCustomers()
      .then((response) => {
        if (cancelled) return;
        const allCustomers = Array.isArray(response?.data) ? response.data : [];
        const rows = buildTbRows(allCustomers);
        setTbBaselineRows(rows);
        setTbDraftRows(rows);
        setTbSheetKey((key) => key + 1);
      })
      .catch((error) => {
        console.error("Failed to load TB data:", error);
        if (!cancelled) {
          setTbBaselineRows([]);
          setTbDraftRows([]);
          window.alert(error?.message || "Failed to load TB data.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTb(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, canView]);

  useEffect(() => {
    if (!canView || activeTab !== "audit") return;
    let cancelled = false;
    setLoadingAudit(true);
    CustomerService.getAllCustomers()
      .then((response) => {
        if (cancelled) return;
        const allCustomers = Array.isArray(response?.data) ? response.data : [];
        const rows = buildAuditRows(allCustomers);
        setAuditBaselineRows(rows);
        setAuditDraftRows(rows);
        setAuditSheetKey((key) => key + 1);
      })
      .catch((error) => {
        console.error("Failed to load Audit data:", error);
        if (!cancelled) {
          setAuditBaselineRows([]);
          setAuditDraftRows([]);
          window.alert(error?.message || "Failed to load Audit data.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAudit(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, canView]);

  useEffect(() => {
    if (!canView || activeTab !== "monthly-note") return;
    let cancelled = false;
    setLoadingMonthlyNote(true);
    CustomerService.getAllCustomers()
      .then((response) => {
        if (cancelled) return;
        const allCustomers = Array.isArray(response?.data) ? response.data : [];
        const rows = buildMonthlyNoteRows(allCustomers);
        setMonthlyNoteBaselineRows(rows);
        setMonthlyNoteDraftRows(rows);
        setMonthlyNoteSheetKey((key) => key + 1);
      })
      .catch((error) => {
        console.error("Failed to load Monthly Note data:", error);
        if (!cancelled) {
          setMonthlyNoteBaselineRows([]);
          setMonthlyNoteDraftRows([]);
          window.alert(error?.message || "Failed to load Monthly Note data.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMonthlyNote(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, canView]);

  useEffect(() => {
    if (!canView || activeTab !== "rounding") return;
    let cancelled = false;
    setLoadingRounding(true);
    MedicalFormService.getRoundingDay()
      .then((response) => {
        if (cancelled) return;
        const data = response?.data || null;
        const rows = roundingRowsFromApi(Array.isArray(data?.rows) ? data.rows : []);
        setRoundingPayload(data);
        setRoundingBaselineRows(rows);
        setRoundingDraftRows(rows);
        setRoundingSheetKey((key) => key + 1);
      })
      .catch((error) => {
        console.error("Failed to load Rounding data:", error);
        if (!cancelled) {
          setRoundingPayload(null);
          setRoundingBaselineRows([]);
          setRoundingDraftRows([]);
          window.alert(
            error?.response?.data?.message || error?.message || "Failed to load Rounding data."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRounding(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, canView]);

  useEffect(() => {
    if (!canView || activeTab !== "adcaps") return;
    let cancelled = false;
    setLoadingAdcaps(true);
    CustomerService.getAllCustomers()
      .then((response) => {
        if (cancelled) return;
        const allCustomers = Array.isArray(response?.data) ? response.data : [];
        const rows = buildAdcapsRows(allCustomers);
        setAdcapsBaselineRows(rows);
        setAdcapsDraftRows(rows);
        setAdcapsSheetKey((key) => key + 1);
      })
      .catch((error) => {
        console.error("Failed to load ADCAPS data:", error);
        if (!cancelled) {
          setAdcapsBaselineRows([]);
          setAdcapsDraftRows([]);
          window.alert(error?.message || "Failed to load ADCAPS data.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAdcaps(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, canView]);

  useEffect(() => {
    if (!canView || activeTab !== "medication") return;
    let cancelled = false;
    setLoadingMedication(true);
    CustomerService.getAllCustomers()
      .then((response) => {
        if (cancelled) return;
        const allCustomers = Array.isArray(response?.data) ? response.data : [];
        const sections = buildMedicationSections(allCustomers);
        setMedicationBaselineSections(sections);
        setMedicationDraftSections(sections);
        setMedicationSheetKey((key) => key + 1);
      })
      .catch((error) => {
        console.error("Failed to load Medication data:", error);
        if (!cancelled) {
          setMedicationBaselineSections(emptyMedicationSections);
          setMedicationDraftSections(emptyMedicationSections);
          window.alert(error?.message || "Failed to load Medication data.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMedication(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, canView]);

  // Pending edits must live in the sheet data: only the active tab's Workbook is
  // mounted, so switching tabs remounts it from this data.
  const molstDisplayEntries = useMemo(() => {
    if (Object.keys(editedCustomers).length === 0) return molstEntries;
    return molstEntries.map((entry) => {
      const changes = editedCustomers[entry.id];
      if (!changes) return entry;
      const merged = mergeMolstEntryChanges(entry, changes);
      return { ...merged, cpr: displayYesNo(merged.cpr) };
    });
  }, [molstEntries, editedCustomers]);

  const molstSheetData = useMemo(
    () => buildMolstFortuneSheetData(molstDisplayEntries, centerName),
    [molstDisplayEntries, centerName]
  );

  const cacfpSheetData = useMemo(
    () => (cacfpPayload ? buildCacfpFortuneSheetData(cacfpPayload) : null),
    [cacfpPayload]
  );

  const vitalSheetData = useMemo(() => {
    if (!vitalPayload) return null;
    return buildVitalSignsFortuneSheetData({
      ...vitalPayload,
      rows: vitalDraftRows.length > 0 ? vitalDraftRows : vitalBaselineRows,
    });
  }, [vitalPayload, vitalBaselineRows, vitalDraftRows]);

  const tbSheetData = useMemo(
    () => buildTbFortuneSheetData(tbDraftRows.length > 0 ? tbDraftRows : tbBaselineRows, siteNum),
    [tbDraftRows, tbBaselineRows, siteNum]
  );

  const auditSheetData = useMemo(
    () =>
      buildAuditFortuneSheetData(
        auditDraftRows.length > 0 ? auditDraftRows : auditBaselineRows,
        siteNum
      ),
    [auditDraftRows, auditBaselineRows, siteNum]
  );

  const monthlyNoteSheetData = useMemo(
    () =>
      buildMonthlyNoteFortuneSheetData(
        monthlyNoteDraftRows.length > 0 ? monthlyNoteDraftRows : monthlyNoteBaselineRows,
        siteNum
      ),
    [monthlyNoteDraftRows, monthlyNoteBaselineRows, siteNum]
  );

  const roundingSheetData = useMemo(
    () => buildRoundingFortuneSheetData(roundingDraftRows, siteNum),
    [roundingDraftRows, siteNum]
  );

  const adcapsSheetData = useMemo(
    () =>
      buildAdcapsFortuneSheetData(
        adcapsDraftRows.length > 0 ? adcapsDraftRows : adcapsBaselineRows,
        siteNum
      ),
    [adcapsDraftRows, adcapsBaselineRows, siteNum]
  );

  const medicationSheetData = useMemo(
    () => buildMedicationFortuneSheetData(medicationDraftSections, siteNum),
    [medicationDraftSections, siteNum]
  );

  useFitFortuneColumnsToWidth(molstWorkbookRef, molstWrapRef, molstSheetData, molstWorkbookKey);
  useFitFortuneColumnsToWidth(cacfpWorkbookRef, cacfpWrapRef, cacfpSheetData, cacfpWorkbookKey);
  useFitFortuneColumnsToWidth(vitalWorkbookRef, vitalWrapRef, vitalSheetData, vitalWorkbookKey);
  useFitFortuneColumnsToWidth(tbWorkbookRef, tbWrapRef, tbSheetData, tbWorkbookKey);
  useFitFortuneColumnsToWidth(auditWorkbookRef, auditWrapRef, auditSheetData, auditWorkbookKey);
  useFitFortuneColumnsToWidth(
    monthlyNoteWorkbookRef,
    monthlyNoteWrapRef,
    monthlyNoteSheetData,
    monthlyNoteWorkbookKey
  );
  useFitFortuneColumnsToWidth(
    roundingWorkbookRef,
    roundingWrapRef,
    roundingSheetData,
    roundingWorkbookKey
  );
  useFitFortuneColumnsToWidth(
    adcapsWorkbookRef,
    adcapsWrapRef,
    adcapsSheetData,
    adcapsWorkbookKey
  );
  useFitFortuneColumnsToWidth(
    medicationWorkbookRef,
    medicationWrapRef,
    medicationSheetData,
    medicationWorkbookKey
  );

  const handleMolstSheetChange = useCallback(
    (workbook) => {
      if (!canEditMolstSheet) return;
      const sheetRows = workbook?.[0]?.data;
      if (!Array.isArray(sheetRows)) return;

      const nextEdits = {};
      molstEntries.forEach((entry, entryIndex) => {
        if (!entry.id) return;
        const row = sheetRows[entryIndex + 2] || [];
        const nextCprRaw = getCellText(row[2]);
        const nextCpr = normaliseYesNo(nextCprRaw);
        const nextMolst = getCellText(row[3]);
        const nextAdvanceDirective = getCellText(row[4]);
        const nextPowerOfAttorney = getCellText(row[5]);
        const nextHospice = getCellText(row[6]);

        const changes = {
          burial_arrangements: nextCpr || normaliseYesNo(entry.cpr),
          molst: nextMolst,
          provisions_for_advance_medical: nextAdvanceDirective,
          power_of_attorney: nextPowerOfAttorney,
          hospice: nextHospice,
        };

        if (hasCustomerChanges(entry, changes)) {
          nextEdits[entry.id] = changes;
        }
      });
      setEditedCustomers((prev) => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(nextEdits);
        if (prevKeys.length === 0 && nextKeys.length === 0) return prev;
        if (
          prevKeys.length === nextKeys.length &&
          nextKeys.every((id) => JSON.stringify(prev[id]) === JSON.stringify(nextEdits[id]))
        ) {
          return prev;
        }
        return nextEdits;
      });
    },
    [molstEntries, canEditMolstSheet]
  );

  const handleVitalSheetChange = useCallback(
    (workbook) => {
      if (!canEdit) return;
      const nextRows = parseVitalSignsRowsFromWorkbook(workbook, vitalBaselineRows);
      // FortuneSheet fires onChange on mount; ignore no-op / display-only diffs.
      setVitalDraftRows((prev) =>
        vitalSignsRowsEqual(nextRows, prev) ? prev : nextRows
      );
    },
    [vitalBaselineRows, canEdit]
  );

  const syncAdcapsDerivedCells = useCallback((draftRows) => {
    const api = adcapsWorkbookRef.current;
    if (!api || !Array.isArray(draftRows)) return;
    draftRows.forEach((row, index) => {
      const r = index + 2;
      try {
        api.setCellValue?.(r, 4, row.nextDueDate || "");
        api.setCellValue?.(r, 5, row.remainingDays === "" || row.remainingDays == null ? "" : `${row.remainingDays}`);
        api.setCellFormat?.(r, 5, "bg", remainingDaysBackground(row.remainingDays) || "#FFFFFF");
      } catch (_err) {
        /* FortuneSheet API may not expose these methods. */
      }
    });
  }, []);

  const handleAdcapsSheetChange = useCallback(
    (workbook) => {
      if (!canEditAdcapsSheet) return;
      const nextRows = parseAdcapsRowsFromWorkbook(workbook, adcapsBaselineRows).map((row) =>
        applyAdcapsDerivedFields(row)
      );
      setAdcapsDraftRows((prev) => (adcapsRowsEqual(nextRows, prev) ? prev : nextRows));

      const sheetRows = workbook?.[0]?.data;
      const derivedStale = nextRows.some((row, index) => {
        const sheetRow = sheetRows?.[index + 2] || [];
        const sheetNext = getCellText(sheetRow[4]);
        const sheetRemaining = getCellText(sheetRow[5]);
        return (
          sheetNext !== `${row.nextDueDate ?? ""}`.trim() ||
          sheetRemaining !== `${row.remainingDays ?? ""}`.trim()
        );
      });
      if (derivedStale) syncAdcapsDerivedCells(nextRows);
    },
    [adcapsBaselineRows, canEditAdcapsSheet, syncAdcapsDerivedCells]
  );

  const handleAdcapsCellUpdate = useCallback((row, column) => {
    if (!canEditAdcapsSheet) return false;
    if (row < 2) return false;
    if (column === 0 || column === 1) return false;
    if (column === 4 || column === 5) return false;
    return column === 2 || column === 3 || column === 6;
  }, [canEditAdcapsSheet]);

  const handleMedicationSheetChange = useCallback(
    (workbook) => {
      if (!canEditMedicationSheet) return;
      const nextSections = parseMedicationSectionsFromWorkbook(workbook, medicationBaselineSections);
      setMedicationDraftSections((prev) =>
        medicationSectionsEqual(nextSections, prev) ? prev : nextSections
      );
    },
    [medicationBaselineSections, canEditMedicationSheet]
  );

  const handleMedicationCellUpdate = useCallback(
    (row, column) => {
      if (!canEditMedicationSheet) return false;
      if (column === 0 || column === 1) return false;
      if (column !== 2 && column !== 3) return false;
      const layout = medicationSheetLayout(medicationBaselineSections);
      const inCenter =
        row >= layout.centerDataStart &&
        row < layout.centerDataStart + layout.centerCount;
      const inBbp =
        row >= layout.bbpDataStart && row < layout.bbpDataStart + layout.bbpCount;
      return inCenter || inBbp;
    },
    [medicationBaselineSections, canEditMedicationSheet]
  );

  const handleTbSheetChange = useCallback(
    (workbook) => {
      if (!canEditTbSheet) return;
      const nextRows = parseTbRowsFromWorkbook(workbook, tbBaselineRows);
      setTbDraftRows((prev) => (tbRowsEqual(nextRows, prev) ? prev : nextRows));
    },
    [tbBaselineRows, canEditTbSheet]
  );

  const handleTbCellUpdate = useCallback((row, column, value) => {
    if (row < 2) return false;
    if (column === 0 || column === 1) return false;
    const next = `${value ?? ""}`.trim();
    if (column === 3) return false;
    if (column === 2) {
      if (!canEditPersonalInfo) return false;
      return next === "Active" || next === "Inactive" || next === "";
    }
    if (column === 4 || column === 5 || column === 6 || column === 7) {
      if (!canEditMedicalInsurance) return false;
      if (column === 4 || column === 5 || column === 6) {
        return next === "Yes" || next === "No" || next === "";
      }
      return true;
    }
    return false;
  }, [canEditPersonalInfo, canEditMedicalInsurance]);

  const handleAuditSheetChange = useCallback(
    (workbook) => {
      if (!canEditAuditSheet) return;
      const nextRows = parseAuditRowsFromWorkbook(workbook, auditBaselineRows).map((row) =>
        applyAuditDerivedFields(row)
      );
      setAuditDraftRows((prev) => (auditRowsEqual(nextRows, prev) ? prev : nextRows));
      const sheetRows = workbook?.[0]?.data;
      const derivedStale = nextRows.some((row, index) => {
        const sheetMonth = getCellText(sheetRows?.[index + 2]?.[4]);
        return sheetMonth !== `${row.auditMonth ?? ""}`.trim();
      });
      if (derivedStale) {
        const api = auditWorkbookRef.current;
        nextRows.forEach((row, index) => {
          try {
            api?.setCellValue?.(index + 2, 4, row.auditMonth || "");
          } catch (_err) {
            /* ignore */
          }
        });
      }
    },
    [auditBaselineRows, canEditAuditSheet]
  );

  const handleAuditCellUpdate = useCallback((row, column) => {
    if (row < 2) return false;
    if (column === 1 || column === 3) return false;
    if (column === 2) return canEditConfidential;
    return false;
  }, [canEditConfidential]);

  const handleMonthlyNoteSheetChange = useCallback(
    (workbook) => {
      if (!canEditMonthlyNoteSheet) return;
      const nextRows = parseMonthlyNoteRowsFromWorkbook(workbook, monthlyNoteBaselineRows).map(
        (row) => applyMonthlyNoteDerivedFields(row)
      );
      setMonthlyNoteDraftRows((prev) => (monthlyNoteRowsEqual(nextRows, prev) ? prev : nextRows));
      const sheetRows = workbook?.[0]?.data;
      const derivedStale = nextRows.some((row, index) => {
        const sheetDate = getCellText(sheetRows?.[index + 2]?.[3]);
        return sheetDate !== `${row.date ?? ""}`.trim();
      });
      if (derivedStale) {
        const api = monthlyNoteWorkbookRef.current;
        nextRows.forEach((row, index) => {
          try {
            api?.setCellValue?.(index + 2, 3, row.date || "");
          } catch (_err) {
            /* ignore */
          }
        });
      }
    },
    [monthlyNoteBaselineRows, canEditMonthlyNoteSheet]
  );

  const handleMonthlyNoteCellUpdate = useCallback(() => false, []);

  const handleRoundingSheetChange = useCallback(
    (workbook) => {
      if (!canEdit) return;
      const nextRows = parseRoundingRowsFromWorkbook(workbook, roundingBaselineRows);
      setRoundingDraftRows((prev) => (roundingRowsEqual(nextRows, prev) ? prev : nextRows));
    },
    [roundingBaselineRows, canEdit]
  );

  const handleRoundingCellUpdate = useCallback(
    (row, column, value) => {
      if (row < 2) return false;
      if (row >= 2 + roundingBaselineRows.length) return false;
      if (column === 0 || column === 1) return false;
      if (column === 2 || column === 4 || column === 9) {
        if (!canEditMedicalInsurance) return false;
        if (column === 2 || column === 4) {
          const next = `${value ?? ""}`.trim();
          return next === "Yes" || next === "No" || next === "";
        }
        return true;
      }
      return column >= 3 && column <= 8;
    },
    [roundingBaselineRows.length, canEditMedicalInsurance]
  );

  const handleRevertMolstChanges = () => {
    setEditedCustomers({});
    setMolstSheetKey((key) => key + 1);
  };

  const handleSaveMolstChanges = async () => {
    if (!canEditMolstSheet) return;
    const pendingUpdates = filterMedicalFormCustomerUpdates(
      Object.entries(editedCustomers).map(([customerId, changes]) => ({
        customerId,
        ...changes,
      })),
      AuthService.canEditCustomerTab
    );
    if (pendingUpdates.length === 0 || savingMolst) return;

    setSavingMolst(true);
    try {
      await Promise.all(
        pendingUpdates.map(({ customerId, ...changes }) =>
          CustomerService.updateCustomer(customerId, changes)
        )
      );
      const savedById = Object.fromEntries(
        pendingUpdates.map(({ customerId, ...changes }) => [customerId, changes])
      );
      setMolstEntries((entries) =>
        sortMolstEntries(
          entries.map((entry) => {
            const changes = savedById[entry.id];
            return changes ? mergeMolstEntryChanges(entry, changes) : entry;
          })
        )
      );
      setEditedCustomers({});
      setMolstSheetKey((key) => key + 1);
    } catch (error) {
      console.error("Failed to save MOLST changes:", error);
      window.alert(error?.message || "Failed to save all MOLST changes. Please try again.");
    } finally {
      setSavingMolst(false);
    }
  };

  const handleRevertVitalChanges = () => {
    setVitalDraftRows(vitalBaselineRows);
    setVitalSheetKey((key) => key + 1);
  };

  const handleSaveVitalChanges = async () => {
    if (!canEdit || savingVital || !vitalPayload) return;
    if (vitalSignsRowsEqual(vitalDraftRows, vitalBaselineRows)) return;

    setSavingVital(true);
    try {
      const rowsToSave = canEditMedicalInsurance
        ? vitalDraftRows
        : vitalDraftRows.map((row) => {
            const baseline =
              vitalBaselineRows.find(
                (item) => `${item?.customer_id || ""}` === `${row?.customer_id || ""}`
              ) || {};
            return {
              ...row,
              diabetes: baseline.diabetes ?? row.diabetes,
              weight: baseline.weight ?? row.weight,
            };
          });
      const customerUpdates = filterMedicalFormCustomerUpdates(
        collectVitalSignsCustomerUpdates(rowsToSave, vitalBaselineRows),
        AuthService.canEditCustomerTab
      );
      if (customerUpdates.length > 0) {
        await Promise.all(
          customerUpdates.map(({ customerId, ...payload }) =>
            CustomerService.updateCustomer(customerId, payload)
          )
        );
      }

      const response = await MedicalFormService.saveVitalSignsDay({
        date: vitalDate,
        title: vitalPayload.title,
        rows: rowsToSave,
      });
      const data = response?.data || null;
      const rawRows = Array.isArray(data?.rows) ? data.rows : rowsToSave;
      const rows = keepVitalSignsRosterRows(
        rawRows.map((row) => ({
          ...row,
          name: vitalSignsCustomerDisplayName(row?.name),
        }))
      );
      setVitalPayload(data || { ...vitalPayload, rows, saved: true });
      setVitalBaselineRows(rows);
      setVitalDraftRows(rows);
      setVitalSheetKey((key) => key + 1);
    } catch (error) {
      console.error("Failed to save Vital Signs chart:", error);
      window.alert(
        error?.response?.data?.message || error?.message || "Failed to save Vital Signs chart."
      );
    } finally {
      setSavingVital(false);
    }
  };

  const handleRevertAdcapsChanges = () => {
    setAdcapsDraftRows(adcapsBaselineRows);
    setAdcapsSheetKey((key) => key + 1);
  };

  const handleRevertMedicationChanges = () => {
    setMedicationDraftSections(medicationBaselineSections);
    setMedicationSheetKey((key) => key + 1);
  };

  const handleRevertTbChanges = () => {
    setTbDraftRows(tbBaselineRows);
    setTbSheetKey((key) => key + 1);
  };

  const handleRevertAuditChanges = () => {
    setAuditDraftRows(auditBaselineRows);
    setAuditSheetKey((key) => key + 1);
  };

  const handleRevertMonthlyNoteChanges = () => {
    setMonthlyNoteDraftRows(monthlyNoteBaselineRows);
    setMonthlyNoteSheetKey((key) => key + 1);
  };

  const handleRevertRoundingChanges = () => {
    setRoundingDraftRows(roundingBaselineRows);
    setRoundingSheetKey((key) => key + 1);
  };

  const handleSaveAdcapsChanges = async () => {
    if (!canEditAdcapsSheet || savingAdcaps) return;
    if (adcapsRowsEqual(adcapsDraftRows, adcapsBaselineRows)) return;

    setSavingAdcaps(true);
    try {
      const customerUpdates = filterMedicalFormCustomerUpdates(
        collectAdcapsCustomerUpdates(adcapsDraftRows, adcapsBaselineRows),
        AuthService.canEditCustomerTab
      );
      if (customerUpdates.length > 0) {
        await Promise.all(
          customerUpdates.map(({ customerId, ...payload }) =>
            CustomerService.updateCustomer(customerId, payload)
          )
        );
      }
      const saved = sortAdcapsRows(
        adcapsDraftRows.map((row) => applyAdcapsDerivedFields(row))
      );
      setAdcapsBaselineRows(saved);
      setAdcapsDraftRows(saved);
      setAdcapsSheetKey((key) => key + 1);
    } catch (error) {
      console.error("Failed to save ADCAPS changes:", error);
      window.alert(error?.message || "Failed to save ADCAPS changes. Please try again.");
    } finally {
      setSavingAdcaps(false);
    }
  };

  const saveCustomerFieldUpdates = async ({
    saving,
    setSaving,
    collect,
    draftRows,
    baselineRows,
    setDraft,
    setBaseline,
    setKey,
    label,
  }) => {
    if (!canEdit || saving) return;
    const rawUpdates = collect(draftRows, baselineRows);
    const customerUpdates = filterMedicalFormCustomerUpdates(
      rawUpdates,
      AuthService.canEditCustomerTab
    );
    if (customerUpdates.length === 0) {
      if (rawUpdates.length > 0) {
        setDraft(baselineRows);
        setKey((key) => key + 1);
        return;
      }
      setDraft(draftRows);
      setBaseline(draftRows);
      setKey((key) => key + 1);
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        customerUpdates.map(({ customerId, ...payload }) =>
          CustomerService.updateCustomer(customerId, payload)
        )
      );
      setBaseline(draftRows);
      setDraft(draftRows);
      setKey((key) => key + 1);
    } catch (error) {
      console.error(`Failed to save ${label} changes:`, error);
      window.alert(error?.message || `Failed to save ${label} changes. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTbChanges = () =>
    saveCustomerFieldUpdates({
      saving: savingTb,
      setSaving: setSavingTb,
      collect: collectTbCustomerUpdates,
      draftRows: tbDraftRows,
      baselineRows: tbBaselineRows,
      setDraft: setTbDraftRows,
      setBaseline: setTbBaselineRows,
      setKey: setTbSheetKey,
      label: "TB",
    });

  const handleSaveAuditChanges = () =>
    saveCustomerFieldUpdates({
      saving: savingAudit,
      setSaving: setSavingAudit,
      collect: collectAuditCustomerUpdates,
      draftRows: auditDraftRows,
      baselineRows: auditBaselineRows,
      setDraft: setAuditDraftRows,
      setBaseline: setAuditBaselineRows,
      setKey: setAuditSheetKey,
      label: "Audit",
    });

  const handleSaveMonthlyNoteChanges = () =>
    saveCustomerFieldUpdates({
      saving: savingMonthlyNote,
      setSaving: setSavingMonthlyNote,
      collect: collectMonthlyNoteCustomerUpdates,
      draftRows: monthlyNoteDraftRows,
      baselineRows: monthlyNoteBaselineRows,
      setDraft: setMonthlyNoteDraftRows,
      setBaseline: setMonthlyNoteBaselineRows,
      setKey: setMonthlyNoteSheetKey,
      label: "Monthly Note",
    });

  const handleSaveMedicationChanges = () =>
    saveCustomerFieldUpdates({
      saving: savingMedication,
      setSaving: setSavingMedication,
      collect: collectMedicationCustomerUpdates,
      draftRows: medicationDraftSections,
      baselineRows: medicationBaselineSections,
      setDraft: setMedicationDraftSections,
      setBaseline: setMedicationBaselineSections,
      setKey: setMedicationSheetKey,
      label: "Medication",
    });

  const handleSaveRoundingChanges = async () => {
    if (!canEdit || savingRounding) return;
    if (roundingRowsEqual(roundingDraftRows, roundingBaselineRows)) return;

    setSavingRounding(true);
    try {
      const rowsToSave = canEditMedicalInsurance
        ? roundingDraftRows
        : roundingDraftRows.map((row) => {
            const baseline =
              roundingBaselineRows.find(
                (item) =>
                  `${item?.customer_id || item?.id || ""}` === `${row?.customer_id || row?.id || ""}`
              ) || {};
            return {
              ...row,
              eyesOn: baseline.eyesOn,
              airtag: baseline.airtag,
              notes: baseline.notes,
            };
          });
      const customerUpdates = filterMedicalFormCustomerUpdates(
        collectRoundingCustomerUpdates(rowsToSave, roundingBaselineRows),
        AuthService.canEditCustomerTab
      );
      if (customerUpdates.length > 0) {
        await Promise.all(
          customerUpdates.map(({ customerId, ...payload }) =>
            CustomerService.updateCustomer(customerId, payload)
          )
        );
      }
      const response = await MedicalFormService.saveRoundingDay({
        rows: rowsToSave,
      });
      const data = response?.data || null;
      const rows = roundingRowsFromApi(
        Array.isArray(data?.rows) ? data.rows : rowsToSave
      );
      setRoundingPayload(data || { ...roundingPayload, rows, saved: true });
      setRoundingBaselineRows(rows);
      setRoundingDraftRows(rows);
      setRoundingSheetKey((key) => key + 1);
    } catch (error) {
      console.error("Failed to save Rounding changes:", error);
      window.alert(
        error?.response?.data?.message || error?.message || "Failed to save Rounding changes. Please try again."
      );
    } finally {
      setSavingRounding(false);
    }
  };

  const handleCprCellUpdate = useCallback((row, column, value) => {
    if (!canEditMolstSheet) return false;
    if (row < 2) return false;
    if (column === 1) return false;
    if (column !== 2) return true;
    const next = `${value ?? ""}`.trim();
    return next === "Yes" || next === "No";
  }, [canEditMolstSheet]);

  const handleVitalDiabetesCellUpdate = useCallback((row, column, value) => {
    // Customer name is read-only. DM/Weight require Customer Info Medical & Insurance edit.
    if (row < 2) return false;
    if (column === 1) return false;
    if (column === 2 || column === 3) {
      if (!canEditMedicalInsurance) return false;
      if (column !== 2) return true;
      const next = `${value ?? ""}`.trim();
      return next === "Yes" || next === "No" || next === "";
    }
    return true;
  }, [canEditMedicalInsurance]);

  const hasUnsavedMolstChanges = canEditMolstSheet && Object.keys(editedCustomers).length > 0;
  const hasUnsavedVitalChanges =
    canEdit && !vitalSignsRowsEqual(vitalDraftRows, vitalBaselineRows);
  const hasUnsavedAdcapsChanges =
    canEditAdcapsSheet && !adcapsRowsEqual(adcapsDraftRows, adcapsBaselineRows);
  const hasUnsavedMedicationChanges =
    canEditMedicationSheet && !medicationSectionsEqual(medicationDraftSections, medicationBaselineSections);
  const hasUnsavedTbChanges = canEditTbSheet && !tbRowsEqual(tbDraftRows, tbBaselineRows);
  const hasUnsavedAuditChanges = canEditAuditSheet && !auditRowsEqual(auditDraftRows, auditBaselineRows);
  const hasUnsavedMonthlyNoteChanges =
    canEditMonthlyNoteSheet && !monthlyNoteRowsEqual(monthlyNoteDraftRows, monthlyNoteBaselineRows);
  const hasUnsavedRoundingChanges =
    canEdit && !roundingRowsEqual(roundingDraftRows, roundingBaselineRows);

  const handleDownloadXlsx = async () => {
    if (exportingXlsx || exportingPdf) return;
    setExportingXlsx(true);
    try {
      if (activeTab === "molst") {
        await downloadMolstXlsx(molstEntries, "MOLST_List", centerName);
      } else if (activeTab === "cacfp") {
        if (!cacfpPayload) throw new Error("CACFP data is not loaded.");
        await downloadCacfpXlsx(cacfpPayload);
      } else if (activeTab === "vital-signs") {
        if (!vitalPayload) throw new Error("Vital Signs data is not loaded.");
        await downloadVitalSignsXlsx({
          ...vitalPayload,
          rows: hasUnsavedVitalChanges ? vitalDraftRows : vitalBaselineRows,
        });
      } else if (activeTab === "tb") {
        await downloadTbXlsx(hasUnsavedTbChanges ? tbDraftRows : tbBaselineRows, siteNum);
      } else if (activeTab === "audit") {
        await downloadAuditXlsx(hasUnsavedAuditChanges ? auditDraftRows : auditBaselineRows, siteNum);
      } else if (activeTab === "monthly-note") {
        await downloadMonthlyNoteXlsx(
          hasUnsavedMonthlyNoteChanges ? monthlyNoteDraftRows : monthlyNoteBaselineRows,
          siteNum
        );
      } else if (activeTab === "rounding") {
        await downloadRoundingXlsx(
          hasUnsavedRoundingChanges ? roundingDraftRows : roundingBaselineRows,
          siteNum
        );
      } else if (activeTab === "adcaps") {
        await downloadAdcapsXlsx(
          hasUnsavedAdcapsChanges ? adcapsDraftRows : adcapsBaselineRows,
          siteNum
        );
      } else if (activeTab === "medication") {
        await downloadMedicationXlsx(
          hasUnsavedMedicationChanges ? medicationDraftSections : medicationBaselineSections,
          siteNum
        );
      }
    } catch (error) {
      console.error("XLSX download failed:", error);
      window.alert(error?.message || "Failed to download XLSX.");
    } finally {
      setExportingXlsx(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (exportingXlsx || exportingPdf) return;
    setExportingPdf(true);
    try {
      if (activeTab === "molst") {
        await downloadMolstPdf(molstEntries, "MOLST_List", centerName);
      } else if (activeTab === "cacfp") {
        if (!cacfpPayload) throw new Error("CACFP data is not loaded.");
        await downloadCacfpPdf(cacfpPayload);
      } else if (activeTab === "vital-signs") {
        if (!vitalPayload) throw new Error("Vital Signs data is not loaded.");
        await downloadVitalSignsPdf({
          ...vitalPayload,
          rows: hasUnsavedVitalChanges ? vitalDraftRows : vitalBaselineRows,
        });
      } else if (activeTab === "tb") {
        await downloadTbPdf(hasUnsavedTbChanges ? tbDraftRows : tbBaselineRows, siteNum);
      } else if (activeTab === "audit") {
        await downloadAuditPdf(hasUnsavedAuditChanges ? auditDraftRows : auditBaselineRows, siteNum);
      } else if (activeTab === "monthly-note") {
        await downloadMonthlyNotePdf(
          hasUnsavedMonthlyNoteChanges ? monthlyNoteDraftRows : monthlyNoteBaselineRows,
          siteNum
        );
      } else if (activeTab === "rounding") {
        await downloadRoundingPdf(
          hasUnsavedRoundingChanges ? roundingDraftRows : roundingBaselineRows,
          siteNum
        );
      } else if (activeTab === "adcaps") {
        await downloadAdcapsPdf(
          hasUnsavedAdcapsChanges ? adcapsDraftRows : adcapsBaselineRows,
          siteNum
        );
      } else if (activeTab === "medication") {
        await downloadMedicationPdf(
          hasUnsavedMedicationChanges ? medicationDraftSections : medicationBaselineSections,
          siteNum
        );
      }
    } catch (error) {
      console.error("PDF download failed:", error);
      window.alert(error?.message || "Failed to download PDF.");
    } finally {
      setExportingPdf(false);
    }
  };

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = maxCacfpYear; y >= 2020; y -= 1) years.push(y);
    return years;
  }, [maxCacfpYear]);

  const vitalWeekOptions = useMemo(() => {
    const today = parseIsoLocalDate(todayIsoDate());
    const currentMonday = getMonday(today);
    const options = [];
    for (let offset = -12; offset <= 12; offset += 1) {
      const monday = new Date(currentMonday);
      monday.setDate(monday.getDate() + offset * 7);
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      options.push({
        value: toIsoLocalDate(monday),
        label: `${formatWeekDate(monday)} - ${formatWeekDate(sunday)}${
          offset === 0 ? " (Current)" : ""
        }`,
      });
    }
    return options;
  }, []);

  const selectedVitalWeek = toIsoLocalDate(getMonday(parseIsoLocalDate(vitalDate)));
  const activeSheetTitle =
    stripExportedOnSuffix(
      normalizeSpace(
        activeTab === "molst"
          ? molstSheetData?.[0]?.celldata?.find((cell) => cell.r === 0 && cell.c === 0)?.v?.m
          : activeTab === "cacfp"
            ? cacfpPayload?.title
            : activeTab === "tb"
              ? tbSheetData?.[0]?.celldata?.find((cell) => cell.r === 0 && cell.c === 0)?.v?.m
              : activeTab === "audit"
                ? auditSheetData?.[0]?.celldata?.find((cell) => cell.r === 0 && cell.c === 0)?.v?.m
                : activeTab === "monthly-note"
                  ? monthlyNoteSheetData?.[0]?.celldata?.find((cell) => cell.r === 0 && cell.c === 0)?.v?.m
                  : activeTab === "rounding"
                    ? roundingSheetData?.[0]?.celldata?.find((cell) => cell.r === 0 && cell.c === 0)?.v?.m
                    : activeTab === "adcaps"
                      ? adcapsSheetData?.[0]?.celldata?.find((cell) => cell.r === 0 && cell.c === 0)?.v?.m
                      : activeTab === "medication"
                        ? medicationSheetData?.[0]?.celldata?.find((cell) => cell.r === 0 && cell.c === 0)?.v?.m
              : vitalPayload?.title
      )
    ) || "Medical Form";

  const toolbarBusy =
    exportingPdf ||
    exportingXlsx ||
    savingMolst ||
    savingVital ||
    (activeTab === "molst" && loadingMolst) ||
    (activeTab === "cacfp" && loadingCacfp) ||
    (activeTab === "vital-signs" && loadingVital) ||
    (activeTab === "tb" && loadingTb) ||
    (activeTab === "audit" && loadingAudit) ||
    (activeTab === "monthly-note" && loadingMonthlyNote) ||
    (activeTab === "rounding" && loadingRounding) ||
    (activeTab === "adcaps" && loadingAdcaps) ||
    (activeTab === "medication" && loadingMedication) ||
    savingAdcaps ||
    savingMedication ||
    savingRounding ||
    savingTb ||
    savingAudit ||
    savingMonthlyNote;

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/dashboard/dashboard">Medical</Breadcrumb.Item>
          <Breadcrumb.Item active>Medical Form</Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
          <h4 className="medical-form-active-title">{activeSheetTitle}</h4>
        </div>
      </div>

      <div className="app-main-content-list-container list-page medical-form-page">
        <div className="app-main-content-list-func-container">
          <div className="medical-form-top-actions">
            {canEditMolstSheet && activeTab === "molst" && hasUnsavedMolstChanges && (
              <>
                <Button
                  variant="primary"
                  disabled={savingMolst}
                  onClick={handleSaveMolstChanges}
                >
                  {savingMolst ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="outline-secondary"
                  disabled={savingMolst}
                  onClick={handleRevertMolstChanges}
                >
                  Revert
                </Button>
              </>
            )}
            {canEdit && activeTab === "vital-signs" && hasUnsavedVitalChanges && (
              <>
                <Button
                  variant="primary"
                  disabled={savingVital}
                  onClick={handleSaveVitalChanges}
                >
                  {savingVital ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="outline-secondary"
                  disabled={savingVital}
                  onClick={handleRevertVitalChanges}
                >
                  Revert
                </Button>
              </>
            )}
            {canEditAdcapsSheet && activeTab === "adcaps" && hasUnsavedAdcapsChanges && (
              <>
                <Button
                  variant="primary"
                  disabled={savingAdcaps}
                  onClick={handleSaveAdcapsChanges}
                >
                  {savingAdcaps ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="outline-secondary"
                  disabled={savingAdcaps}
                  onClick={handleRevertAdcapsChanges}
                >
                  Revert
                </Button>
              </>
            )}
            {canEditMedicationSheet && activeTab === "medication" && hasUnsavedMedicationChanges && (
              <>
                <Button
                  variant="primary"
                  disabled={savingMedication}
                  onClick={handleSaveMedicationChanges}
                >
                  {savingMedication ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="outline-secondary"
                  disabled={savingMedication}
                  onClick={handleRevertMedicationChanges}
                >
                  Revert
                </Button>
              </>
            )}
            {canEditTbSheet && activeTab === "tb" && hasUnsavedTbChanges && (
              <>
                <Button variant="primary" disabled={savingTb} onClick={handleSaveTbChanges}>
                  {savingTb ? "Saving…" : "Save"}
                </Button>
                <Button variant="outline-secondary" disabled={savingTb} onClick={handleRevertTbChanges}>
                  Revert
                </Button>
              </>
            )}
            {canEditAuditSheet && activeTab === "audit" && hasUnsavedAuditChanges && (
              <>
                <Button variant="primary" disabled={savingAudit} onClick={handleSaveAuditChanges}>
                  {savingAudit ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="outline-secondary"
                  disabled={savingAudit}
                  onClick={handleRevertAuditChanges}
                >
                  Revert
                </Button>
              </>
            )}
            {canEditMonthlyNoteSheet && activeTab === "monthly-note" && hasUnsavedMonthlyNoteChanges && (
              <>
                <Button
                  variant="primary"
                  disabled={savingMonthlyNote}
                  onClick={handleSaveMonthlyNoteChanges}
                >
                  {savingMonthlyNote ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="outline-secondary"
                  disabled={savingMonthlyNote}
                  onClick={handleRevertMonthlyNoteChanges}
                >
                  Revert
                </Button>
              </>
            )}
            {canEdit && activeTab === "rounding" && hasUnsavedRoundingChanges && (
              <>
                <Button
                  variant="primary"
                  disabled={savingRounding}
                  onClick={handleSaveRoundingChanges}
                >
                  {savingRounding ? "Saving…" : "Save"}
                </Button>
                <Button
                  variant="outline-secondary"
                  disabled={savingRounding}
                  onClick={handleRevertRoundingChanges}
                >
                  Revert
                </Button>
              </>
            )}
            <Dropdown className="medical-form-export-dropdown" align="end">
              <Dropdown.Toggle
                variant="primary"
                disabled={toolbarBusy}
                id="medical-form-export"
              >
                <Download size={16} className="me-2" aria-hidden />
                {exportingPdf || exportingXlsx ? "Exporting…" : "Export"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item disabled={toolbarBusy} onClick={handleDownloadPdf}>
                  PDF
                </Dropdown.Item>
                <Dropdown.Item disabled={toolbarBusy} onClick={handleDownloadXlsx}>
                  XLSX
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <Tabs
            activeKey={activeTab}
            onSelect={(key) => {
              if (!key) return;
              if (activeTab === "molst" && hasUnsavedMolstChanges) {
                const ok = window.confirm("You have unsaved MOLST changes. Switch tabs anyway?");
                if (!ok) return;
              }
              if (activeTab === "vital-signs" && hasUnsavedVitalChanges) {
                const ok = window.confirm(
                  "You have unsaved Vital Sign changes. Switch tabs anyway?"
                );
                if (!ok) return;
              }
              if (activeTab === "adcaps" && hasUnsavedAdcapsChanges) {
                const ok = window.confirm(
                  "You have unsaved ADCAPS changes. Switch tabs anyway?"
                );
                if (!ok) return;
              }
              if (activeTab === "medication" && hasUnsavedMedicationChanges) {
                const ok = window.confirm(
                  "You have unsaved Medication changes. Switch tabs anyway?"
                );
                if (!ok) return;
              }
              if (activeTab === "tb" && hasUnsavedTbChanges) {
                const ok = window.confirm("You have unsaved TB changes. Switch tabs anyway?");
                if (!ok) return;
              }
              if (activeTab === "audit" && hasUnsavedAuditChanges) {
                const ok = window.confirm("You have unsaved Audit changes. Switch tabs anyway?");
                if (!ok) return;
              }
              if (activeTab === "monthly-note" && hasUnsavedMonthlyNoteChanges) {
                const ok = window.confirm(
                  "You have unsaved Monthly Note changes. Switch tabs anyway?"
                );
                if (!ok) return;
              }
              if (activeTab === "rounding" && hasUnsavedRoundingChanges) {
                const ok = window.confirm(
                  "You have unsaved Rounding changes. Switch tabs anyway?"
                );
                if (!ok) return;
              }
              setActiveTab(key);
            }}
            id="medical-form-tabs"
          >
            <Tab eventKey="molst" title="MOLST">
              <div className="medical-form-tab-toolbar">
                <span className="text-muted small">
                  {canEditMolstSheet
                    ? "CPR, MOLST, Advance Directive, Power of Attorney, and Hospice save to Customer Info."
                    : canEdit
                      ? medicalFormCustomerLockHint("Customer Info Medical & Insurance edit")
                      : "View only — editing requires Medical Form edit permission."}
                </span>
              </div>
              {loadingMolst ? (
                <div className="d-flex align-items-center gap-2 p-4 text-muted">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                  <span>Loading MOLST sheet…</span>
                </div>
              ) : activeTab === "molst" ? (
                  <div className="medical-form-sheet-wrap" ref={molstWrapRef}>
                    <MedicalFormWorkbook
                      ref={molstWorkbookRef}
                      key={molstWorkbookKey}
                      data={molstSheetData}
                      showToolbar={false}
                      showFormulaBar={false}
                      showSheetTabs={false}
                      rowHeaderWidth={0}
                      columnHeaderHeight={0}
                      {...fortuneViewOrEditProps(canEditMolstSheet, {
                        onChange: handleMolstSheetChange,
                        hooks: {
                          beforeUpdateCell: handleCprCellUpdate,
                          beforePaste: blockPasteOnCustomerNameColumn,
                        },
                      })}
                    />
                  </div>
              ) : null}
            </Tab>

            <Tab eventKey="cacfp" title="CACFP">
              <div className="medical-form-tab-toolbar">
                <Form.Group className="medical-form-year-picker mb-0">
                  <Form.Label className="small mb-1">Year</Form.Label>
                  <Form.Select
                    size="sm"
                    value={cacfpYear}
                    disabled={loadingCacfp}
                    onChange={(e) => setCacfpYear(parseInt(e.target.value, 10))}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year - 1}-{year}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <span className="text-muted small">
                  View only — does not save to customer info.
                </span>
              </div>
              {loadingCacfp ? (
                <div className="d-flex align-items-center gap-2 p-4 text-muted">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                  <span>Loading CACFP sheet…</span>
                </div>
              ) : cacfpSheetData && activeTab === "cacfp" ? (
                <div className="medical-form-sheet-wrap" ref={cacfpWrapRef}>
                      <MedicalFormWorkbook
                    ref={cacfpWorkbookRef}
                    key={cacfpWorkbookKey}
                    data={cacfpSheetData}
                    showToolbar={false}
                    showFormulaBar={false}
                    showSheetTabs={false}
                    rowHeaderWidth={0}
                    columnHeaderHeight={0}
                    allowEdit={false}
                    hooks={{}}
                  />
                </div>
              ) : (
                <div className="p-4 text-muted">No CACFP data for this year.</div>
              )}
            </Tab>

            <Tab eventKey="vital-signs" title="Vital Sign">
              <div className="medical-form-tab-toolbar">
                <Form.Group className="medical-form-date-picker mb-0">
                  <Form.Label className="small mb-1">Chart date</Form.Label>
                  <Form.Select
                    size="sm"
                    value={selectedVitalWeek}
                    disabled={loadingVital || savingVital}
                    onChange={(e) => {
                      if (hasUnsavedVitalChanges) {
                        const ok = window.confirm(
                          "You have unsaved Vital Sign changes. Change date anyway?"
                        );
                        if (!ok) return;
                      }
                      const nextWeek = e.target.value;
                      const currentWeek = toIsoLocalDate(
                        getMonday(parseIsoLocalDate(todayIsoDate()))
                      );
                      setVitalDate(nextWeek === currentWeek ? todayIsoDate() : nextWeek);
                    }}
                  >
                    {vitalWeekOptions.map((week) => (
                      <option key={week.value} value={week.value}>
                        {week.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <span className="text-muted small">
                  {canEdit
                    ? canEditMedicalInsurance
                      ? "Week chart saves to a dedicated week + site file. DM and Weight edits also update Customer Info."
                      : "Week chart saves to a dedicated week + site file. DM and Weight are locked without Customer Info Medical & Insurance edit."
                    : "View only — editing requires Medical Form edit permission."}
                  {vitalPayload?.saved ? " Loaded saved chart." : " New chart (not saved yet)."}
                </span>
              </div>
              {loadingVital ? (
                <div className="d-flex align-items-center gap-2 p-4 text-muted">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                  <span>Loading Vital Sign sheet…</span>
                </div>
              ) : vitalSheetData && activeTab === "vital-signs" ? (
                  <div className="medical-form-sheet-wrap" ref={vitalWrapRef}>
                    <MedicalFormWorkbook
                      ref={vitalWorkbookRef}
                      key={vitalWorkbookKey}
                      data={vitalSheetData}
                      showToolbar={false}
                      showFormulaBar={false}
                      showSheetTabs={false}
                      rowHeaderWidth={0}
                      columnHeaderHeight={0}
                      {...fortuneViewOrEditProps(canEdit, {
                        onChange: handleVitalSheetChange,
                        hooks: {
                          beforeUpdateCell: handleVitalDiabetesCellUpdate,
                          beforePaste: blockPasteOnCustomerNameColumn,
                        },
                      })}
                    />
                  </div>
              ) : (
                <div className="p-4 text-muted">No Vital Sign data for this date.</div>
              )}
            </Tab>

            <Tab eventKey="tb" title="TB">
              <div className="medical-form-tab-toolbar">
                <span className="text-muted small">
                  {canEditTbSheet
                    ? "Status, TB Form, Prescreening, 45 Days Admission Note, and TB Notes save to Customer Info. Admission Date can only be changed in Customer Info."
                    : canEdit
                      ? medicalFormCustomerLockHint("Customer Info Personal Info or Medical & Insurance edit")
                      : "View only — editing requires Medical Form edit permission."}
                </span>
              </div>
              {loadingTb ? (
                <div className="d-flex align-items-center gap-2 p-4 text-muted">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                  <span>Loading TB sheet…</span>
                </div>
              ) : tbSheetData && activeTab === "tb" ? (
                <div className="medical-form-sheet-wrap" ref={tbWrapRef}>
                      <MedicalFormWorkbook
                    ref={tbWorkbookRef}
                    key={tbWorkbookKey}
                    data={tbSheetData}
                    showToolbar={false}
                    showFormulaBar={false}
                    showSheetTabs={false}
                    rowHeaderWidth={0}
                    columnHeaderHeight={0}
                    {...fortuneViewOrEditProps(canEditTbSheet, {
                      onChange: handleTbSheetChange,
                      hooks: {
                        beforeUpdateCell: handleTbCellUpdate,
                        beforePaste: blockPasteOnTbLockedColumns,
                      },
                    })}
                  />
                </div>
              ) : (
                <div className="p-4 text-muted">No TB data.</div>
              )}
            </Tab>

            <Tab eventKey="audit" title="Audit">
              <div className="medical-form-tab-toolbar">
                <span className="text-muted small">
                  {canEditAuditSheet
                    ? "Medicaid Number saves to Customer Info. Admission Date can only be changed in Customer Info. Audit Month is the admission month."
                    : canEdit
                      ? medicalFormCustomerLockHint("Customer Info Confidential Details or Personal Info edit")
                      : "View only — editing requires Medical Form edit permission."}
                </span>
              </div>
              {loadingAudit ? (
                <div className="d-flex align-items-center gap-2 p-4 text-muted">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                  <span>Loading Audit sheet…</span>
                </div>
              ) : auditSheetData && activeTab === "audit" ? (
                <div className="medical-form-sheet-wrap" ref={auditWrapRef}>
                      <MedicalFormWorkbook
                    ref={auditWorkbookRef}
                    key={auditWorkbookKey}
                    data={auditSheetData}
                    showToolbar={false}
                    showFormulaBar={false}
                    showSheetTabs={false}
                    rowHeaderWidth={0}
                    columnHeaderHeight={0}
                    {...fortuneViewOrEditProps(canEditAuditSheet, {
                      onChange: handleAuditSheetChange,
                      hooks: {
                        beforeUpdateCell: handleAuditCellUpdate,
                        beforePaste: blockPasteOnAuditLockedColumns,
                      },
                    })}
                  />
                </div>
              ) : (
                <div className="p-4 text-muted">No Audit data.</div>
              )}
            </Tab>

            <Tab eventKey="monthly-note" title="Monthly Note">
              <div className="medical-form-tab-toolbar">
                <span className="text-muted small">
                  {canEdit
                    ? "Admission Date can only be changed in Customer Info. Date is the admission day of the month."
                    : "View only — editing requires Medical Form edit permission."}
                </span>
              </div>
              {loadingMonthlyNote ? (
                <div className="d-flex align-items-center gap-2 p-4 text-muted">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                  <span>Loading Monthly Note sheet…</span>
                </div>
              ) : monthlyNoteSheetData && activeTab === "monthly-note" ? (
                <div className="medical-form-sheet-wrap" ref={monthlyNoteWrapRef}>
                      <MedicalFormWorkbook
                    ref={monthlyNoteWorkbookRef}
                    key={monthlyNoteWorkbookKey}
                    data={monthlyNoteSheetData}
                    showToolbar={false}
                    showFormulaBar={false}
                    showSheetTabs={false}
                    rowHeaderWidth={0}
                    columnHeaderHeight={0}
                    {...fortuneViewOrEditProps(canEditMonthlyNoteSheet, {
                      onChange: handleMonthlyNoteSheetChange,
                      hooks: {
                        beforeUpdateCell: handleMonthlyNoteCellUpdate,
                        beforePaste: blockPasteOnMonthlyNoteLockedColumns,
                      },
                    })}
                  />
                </div>
              ) : (
                <div className="p-4 text-muted">No Monthly Note data.</div>
              )}
            </Tab>

            <Tab eventKey="rounding" title="Rounding">
              <div className="medical-form-tab-toolbar">
                <span className="text-muted small">
                  {canEdit
                    ? canEditMedicalInsurance
                      ? "Eyes-On, Airtag, and Rounding Notes save to Customer Info (new value replaces the previous). Assist seating, staff, and time slots save to one chart file for this site."
                      : "Assist seating, staff, and time slots save to one chart file for this site. Eyes-On, Airtag, and Rounding Notes are locked without Customer Info Medical & Insurance edit."
                    : "View only — editing requires Medical Form edit permission."}
                  {roundingPayload?.saved ? " Loaded saved chart." : " New chart (not saved yet)."}
                </span>
              </div>
              {loadingRounding ? (
                <div className="d-flex align-items-center gap-2 p-4 text-muted">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                  <span>Loading Rounding sheet…</span>
                </div>
              ) : roundingSheetData && activeTab === "rounding" ? (
                <div className="medical-form-sheet-wrap" ref={roundingWrapRef}>
                      <MedicalFormWorkbook
                    ref={roundingWorkbookRef}
                    key={roundingWorkbookKey}
                    data={roundingSheetData}
                    showToolbar={false}
                    showFormulaBar={false}
                    showSheetTabs={false}
                    rowHeaderWidth={0}
                    columnHeaderHeight={0}
                    {...fortuneViewOrEditProps(canEdit, {
                      onChange: handleRoundingSheetChange,
                      hooks: {
                        beforeUpdateCell: handleRoundingCellUpdate,
                        beforePaste: blockPasteOnNoAndCustomerNameColumns,
                      },
                    })}
                  />
                </div>
              ) : (
                <div className="p-4 text-muted">No Rounding data.</div>
              )}
            </Tab>

            <Tab eventKey="adcaps" title="ADCAPS">
              <div className="medical-form-tab-toolbar">
                <span className="text-muted small">
                  {canEditAdcapsSheet
                    ? "Active Medicaid members. LTSS ID Number, Adcaps Completed Date, and ADCAPS Note save to Customer Info (note replaces the previous value). Next due date is completed date + 120 days."
                    : canEdit
                      ? medicalFormCustomerLockHint("Customer Info Medical & Insurance edit")
                      : "View only — editing requires Medical Form edit permission."}
                </span>
              </div>
              {loadingAdcaps ? (
                <div className="d-flex align-items-center gap-2 p-4 text-muted">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                  <span>Loading ADCAPS sheet…</span>
                </div>
              ) : adcapsSheetData && activeTab === "adcaps" ? (
                <div className="medical-form-sheet-wrap" ref={adcapsWrapRef}>
                      <MedicalFormWorkbook
                    ref={adcapsWorkbookRef}
                    key={adcapsWorkbookKey}
                    data={adcapsSheetData}
                    showToolbar={false}
                    showFormulaBar={false}
                    showSheetTabs={false}
                    rowHeaderWidth={0}
                    columnHeaderHeight={0}
                    {...fortuneViewOrEditProps(canEditAdcapsSheet, {
                      onChange: handleAdcapsSheetChange,
                      hooks: {
                        beforeUpdateCell: handleAdcapsCellUpdate,
                        beforePaste: blockPasteOnCustomerNameColumn,
                      },
                    })}
                  />
                </div>
              ) : (
                <div className="p-4 text-muted">No ADCAPS data.</div>
              )}
            </Tab>

            <Tab eventKey="medication" title="Medication">
              <div className="medical-form-tab-toolbar">
                <span className="text-muted small">
                  {canEditMedicationSheet
                    ? "Active members. Type and Medication Note save to Customer Info in the first section; Pillbox Type and Pillbox Note in the second. Roster membership (Yes/No) is unchanged here."
                    : canEdit
                      ? medicalFormCustomerLockHint("Customer Info Medical & Insurance edit")
                      : "View only — editing requires Medical Form edit permission."}
                </span>
              </div>
              {loadingMedication ? (
                <div className="d-flex align-items-center gap-2 p-4 text-muted">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                  <span>Loading Medication sheet…</span>
                </div>
              ) : medicationSheetData && activeTab === "medication" ? (
                <div className="medical-form-sheet-wrap" ref={medicationWrapRef}>
                      <MedicalFormWorkbook
                    ref={medicationWorkbookRef}
                    key={medicationWorkbookKey}
                    data={medicationSheetData}
                    showToolbar={false}
                    showFormulaBar={false}
                    showSheetTabs={false}
                    rowHeaderWidth={0}
                    columnHeaderHeight={0}
                    {...fortuneViewOrEditProps(canEditMedicationSheet, {
                      onChange: handleMedicationSheetChange,
                      hooks: {
                        beforeUpdateCell: handleMedicationCellUpdate,
                        beforePaste: blockPasteOnNoAndCustomerNameColumns,
                      },
                    })}
                  />
                </div>
              ) : (
                <div className="p-4 text-muted">No Medication data.</div>
              )}
            </Tab>
          </Tabs>

        </div>
      </div>
    </>
  );
};

const CUSTOMER_NAME_COLUMN = 1;
const NO_COLUMN = 0;

function selectionOverlapsColumns(selection, columns) {
  const ranges = Array.isArray(selection) ? selection : [];
  const locked = Array.isArray(columns) ? columns : [];
  return ranges.some((range) => {
    const cols = range?.column;
    if (!Array.isArray(cols) || cols.length === 0) return false;
    const start = Number(cols[0]);
    const end = Number(cols[cols.length - 1] ?? cols[0]);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    const lo = Math.min(start, end);
    const hi = Math.max(start, end);
    return locked.some((col) => col >= lo && col <= hi);
  });
}

function blockPasteOnCustomerNameColumn(selection) {
  return !selectionOverlapsColumns(selection, [CUSTOMER_NAME_COLUMN]);
}

const TB_ADMISSION_DATE_COLUMN = 3;
const AUDIT_ADMISSION_DATE_COLUMN = 3;
const MONTHLY_NOTE_ADMISSION_DATE_COLUMN = 2;

function blockPasteOnTbLockedColumns(selection) {
  return !selectionOverlapsColumns(selection, [CUSTOMER_NAME_COLUMN, TB_ADMISSION_DATE_COLUMN]);
}

function blockPasteOnAuditLockedColumns(selection) {
  return !selectionOverlapsColumns(selection, [CUSTOMER_NAME_COLUMN, AUDIT_ADMISSION_DATE_COLUMN]);
}

function blockPasteOnMonthlyNoteLockedColumns(selection) {
  return !selectionOverlapsColumns(selection, [CUSTOMER_NAME_COLUMN, MONTHLY_NOTE_ADMISSION_DATE_COLUMN]);
}

function blockPasteOnNoAndCustomerNameColumns(selection) {
  return !selectionOverlapsColumns(selection, [NO_COLUMN, CUSTOMER_NAME_COLUMN]);
}

function normalizeSpace(value) {
  return `${value ?? ""}`.replace(/\s+/g, " ").trim();
}

/** Display-only: keep full title (with Exported on…) in PDF/XLSX builders. */
function stripExportedOnSuffix(title) {
  return `${title ?? ""}`.replace(/\s*\(Exported on [^)]+\)\s*$/i, "").trim();
}

function getCellText(cell) {
  if (cell == null) return "";
  const raw = typeof cell === "object" ? `${cell.m ?? cell.v ?? ""}` : `${cell}`;
  // FortuneSheet may wrap long text with newlines on mount; ignore that as an edit.
  return normalizeSpace(raw);
}

function displayYesNo(value) {
  const normalised = normaliseYesNo(value);
  if (normalised === "yes") return "Yes";
  if (normalised === "no") return "No";
  return `${value ?? ""}`;
}

function normaliseYesNo(value) {
  const normalised = `${value ?? ""}`.trim().toLowerCase();
  if (normalised === "yes") return "yes";
  if (normalised === "no") return "no";
  return "";
}

function hasCustomerChanges(entry, changes) {
  return (
    normaliseYesNo(entry.cpr) !== changes.burial_arrangements ||
    normalizeSpace(entry.molst) !== normalizeSpace(changes.molst) ||
    normalizeSpace(entry.advanceDirective) !==
      normalizeSpace(changes.provisions_for_advance_medical) ||
    normalizeSpace(entry.powerOfAttorney) !== normalizeSpace(changes.power_of_attorney) ||
    normalizeSpace(entry.hospice) !== normalizeSpace(changes.hospice)
  );
}

function mergeMolstEntryChanges(entry, changes) {
  return {
    ...entry,
    cpr: changes.burial_arrangements,
    molst: changes.molst,
    advanceDirective: changes.provisions_for_advance_medical,
    powerOfAttorney: changes.power_of_attorney,
    hospice: changes.hospice,
  };
}

const MedicalFormPage = () => (
  <MedicalFormErrorBoundary
    fallback={
      <div className="list row mb-4">
        <div className="col-md-12 text-primary">
          <h4>Medical Form</h4>
          <p className="text-muted mt-3">
            Unable to display Medical Form. Refresh the page. View-only access should still work after a refresh.
          </p>
        </div>
      </div>
    }
  >
    <MedicalForm />
  </MedicalFormErrorBoundary>
);

export default MedicalFormPage;
